import React from "react";
import { Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import Home from "../../containers/Home/Home";
import ServicePage from "../../containers/ServicePage/ServicePage";
import LocationPage from "../../containers/LocationPage/LocationPage";
import Team from "../../containers/Team/Team";
import Injuries from "../../containers/Injuries/Injuries";
import Contact from "../../containers/Contact/Contact";
import RequestAppointment from "../../containers/RequestAppointment/RequestAppointment";
import NotFound from "../../containers/NotFound/NotFound";
import BlogIndex from "../../containers/Blog/BlogIndex";
import BlogPost from "../../containers/Blog/BlogPost";
import BlogCategory from "../../containers/Blog/BlogCategory";
import Resources from "../../containers/Resources/Resources";
import FAQ from "../../containers/FAQ/FAQ";
import CoreExercises from "../../containers/CoreExercises/CoreExercises";
import MediaCenter from "../../containers/MediaCenter/MediaCenter";
import Videos from "../../containers/Videos/Videos";
import PatientReferral from "../../containers/PatientReferral/PatientReferral";
import NewPatientForms from "../../containers/NewPatientForms/NewPatientForms";
import InjuryHub from "../../containers/InjuryHub/InjuryHub";
import SpecialtyPage from "../../containers/SpecialtyPage/SpecialtyPage";
import AboutUs from "../../containers/AboutUs/AboutUs";
import Services from "../../containers/Services/Services";
import Locations from "../../containers/Locations/Locations";
import PrivacyPolicy from "../../containers/PrivacyPolicy/PrivacyPolicy";
import LegalPage from "../../containers/LegalPage/LegalPage";
import ConditionPage from "../../containers/ConditionPage/ConditionPage";
import LegalIntent from "../../containers/LegalIntent/LegalIntent";
import BodyPartPage from "../../containers/BodyPartPage/BodyPartPage";

/**
 * Language-prefixed route wrapper.
 * Generates routes for all 6 locales: /, /es, /pt, /ko, /ja, /fr
 */
function LangRoutes({ path, element }) {
  const prefixes = ['', '/es', '/pt', '/ko', '/ja', '/fr'];
  return prefixes.map(prefix => (
    <Route key={prefix + path} path={prefix + path} element={element} />
  ));
}

/**
 * Known locale codes for explicit locale-in-URL pSEO routes.
 * These routes use /:locale/ as the first segment (e.g. /en/, /es/, etc.)
 * React Router ranks static segments higher than dynamic ones, so
 * /en/:slug beats /:locationSlug/:serviceSlug when first segment === 'en'.
 */
const LOCALE_CODES = ['en', 'es', 'pt', 'ko', 'ja', 'fr'];

/**
 * Body part slugs that should resolve to BodyPartPage
 * when accessed via /:locale/:bodyPartSlug
 */
const BODY_PART_SLUGS = [
  'back-injury', 'knee-injury', 'neck-injury', 'shoulder-injury',
  'hip-injury', 'wrist-injury', 'ankle-injury', 'head-injury',
  'elbow-injury', 'foot-injury', 'hand-injury', 'spine-injury',
];

export default function Routes() {
  return (
    <RouterRoutes>
      {/* ─── Home ─── */}
      {LangRoutes({ path: '/', element: <Home /> })}

      {/* ─── Global / Static pages ─── */}
      {LangRoutes({ path: '/about-us', element: <AboutUs /> })}
      {LangRoutes({ path: '/services', element: <Services /> })}
      {LangRoutes({ path: '/locations', element: <Locations /> })}
      {LangRoutes({ path: '/meet-our-team', element: <Team /> })}
      {LangRoutes({ path: '/meet-our-team/:doctorSlug', element: <Team /> })}
      {LangRoutes({ path: '/injuries', element: <Injuries /> })}
      {LangRoutes({ path: '/contact', element: <Contact /> })}
      {LangRoutes({ path: '/request-appt', element: <RequestAppointment /> })}
      {/* Legacy redirect: old URLs may be indexed under /request-appointment */}
      {LangRoutes({ path: '/request-appointment', element: <Navigate to="/request-appt" replace /> })}
      {LangRoutes({ path: '/privacy-policy', element: <Navigate to="/privacy" replace /> })}
      {LangRoutes({ path: '/privacy', element: <LegalPage slug="privacy" /> })}
      {LangRoutes({ path: '/terms', element: <LegalPage slug="terms" /> })}
      {LangRoutes({ path: '/sms-messaging-terms-conditions', element: <Navigate to="/terms" replace /> })}
      {LangRoutes({ path: '/ada', element: <LegalPage slug="ada" /> })}
      {LangRoutes({ path: '/accessibility', element: <Navigate to="/ada" replace /> })}
      {LangRoutes({ path: '/sitemap', element: <LegalPage slug="sitemap" /> })}

      {/* ─── Resource pages ─── */}
      {LangRoutes({ path: '/resources', element: <Resources /> })}
      {LangRoutes({ path: '/blog', element: <BlogIndex /> })}
      {LangRoutes({ path: '/blog/category/:categorySlug', element: <BlogCategory /> })}
      {LangRoutes({ path: '/blog/:postSlug', element: <BlogPost /> })}
      {LangRoutes({ path: '/frequently-asked-questions', element: <FAQ /> })}
      {LangRoutes({ path: '/faqs', element: <FAQ /> })}
      {LangRoutes({ path: '/faqs-injured-in-a-car-accident', element: <FAQ /> })}
      {LangRoutes({ path: '/workplace-injury-questions', element: <FAQ /> })}
      {LangRoutes({ path: '/sports-injury-faq', element: <FAQ /> })}
      {LangRoutes({ path: '/core-exercises', element: <CoreExercises /> })}
      {LangRoutes({ path: '/media', element: <MediaCenter /> })}
      {LangRoutes({ path: '/media-center', element: <MediaCenter /> })}
      {LangRoutes({ path: '/videos', element: <Videos /> })}
      {LangRoutes({ path: '/resources/patient-referral', element: <PatientReferral /> })}
      {LangRoutes({ path: '/new-patient-forms', element: <NewPatientForms /> })}

      {/* ─── Injury hub pages (existing /injuries/:slug pattern) ─── */}
      {LangRoutes({ path: '/injuries/:injurySlug', element: <InjuryHub /> })}

      {/* ─── Specialty pages ─── */}
      {LangRoutes({ path: '/specialty/:specialtySlug', element: <SpecialtyPage /> })}

      {/* ─── Condition pages (existing /conditions/:slug) ─── */}
      {LangRoutes({ path: '/conditions/:conditionSlug', element: <ConditionPage /> })}

      {/* ─── Legal intent pages (existing /legal/:slug) ─── */}
      {LangRoutes({ path: '/legal/:intentSlug', element: <LegalIntent /> })}


      {/* ═══════════════════════════════════════════════════════════
          pSEO MATRIX ROUTES — Explicit locale prefix in URL
          Pattern: /:locale/:city/:service (540+420+360 = 1,320 pages)
          e.g. /en/atlanta/car-accident-doctor
          These use /:locationSlug/:serviceSlug param names so
          ServicePage.js can reuse its existing param destructuring.
          NOTE: 3-segment routes are inherently more specific than
          2-segment ones and are matched first by React Router v6.
      ═══════════════════════════════════════════════════════════ */}

      {/* pSEO: locale + city + service (3-segment) */}
      {LOCALE_CODES.map((locale) => (
        <Route
          key={`pseo-service-${locale}`}
          path={`/${locale}/:locationSlug/:serviceSlug`}
          element={<ServicePage />}
        />
      ))}

      {/* pSEO: locale + conditions + condition slug */}
      {LOCALE_CODES.map((locale) => (
        <Route
          key={`pseo-condition-${locale}`}
          path={`/${locale}/conditions/:conditionSlug`}
          element={<ConditionPage />}
        />
      ))}

      {/* pSEO: locale + legal + intent slug */}
      {LOCALE_CODES.map((locale) => (
        <Route
          key={`pseo-legal-${locale}`}
          path={`/${locale}/legal/:intentSlug`}
          element={<LegalIntent />}
        />
      ))}

      {/* pSEO: locale + body part injury slug (2-segment, static locale wins over dynamic /:locationSlug) */}
      {LOCALE_CODES.flatMap((locale) =>
        BODY_PART_SLUGS.map((slug) => (
          <Route
            key={`pseo-bodypart-${locale}-${slug}`}
            path={`/${locale}/${slug}`}
            element={<BodyPartPage />}
          />
        ))
      )}

      {/* ─── City landing page (2-segment, no locale prefix) ─── */}
      {LangRoutes({ path: '/:locationSlug', element: <LocationPage /> })}

      {/* ─── City + Service page (2-segment, no locale prefix) ─── */}
      {LangRoutes({ path: '/:locationSlug/:serviceSlug', element: <ServicePage /> })}

      {/* ─── 404 ─── */}
      <Route path="*" element={<NotFound />} />
    </RouterRoutes>
  );
}
